import { Deferred, Head, usePage } from '@inertiajs/react'
import { withAppLayout, PageHeading } from '@/components/layout'
import { PaymentActions } from '@/components/payments/PaymentActions'
import type { SharedProps } from '@/types'
import {
  Button,
  Card,
  CardTitle,
  DescriptionList,
  DescriptionTerm,
  DescriptionDetails,
} from '@/components/ui'
import { TransactionStatusBadge } from '@/components/common/TransactionStatusBadge'
import { TransactionActivity } from '@/components/common/TransactionActivity'
import { GatewayNotice } from '@/components/common/GatewayNotice'
import { CardSkeleton } from '@/components/common/Skeletons'
import { useTranslations } from '@/lib/i18n'
import { formatMoney, formatDateTime } from '@/lib/format'
import type { GatewayState, GatewayError, PaymentRecord, ActivityRow } from '@/types'

interface PaymentShowProps {
  payment?: { record: PaymentRecord | null; error: GatewayError | null }
  activity: ActivityRow[]
  gateway: GatewayState
}

function PaymentShow({ payment, activity, gateway }: PaymentShowProps) {
  const { t, locale } = useTranslations()
  const { props } = usePage<SharedProps>()
  const canWrite = ['superadmin', 'admin', 'operator'].includes(props.auth.user?.role ?? '')
  const record = payment?.record ?? null
  const money = (value: string) => formatMoney(value, record?.currency ?? 'EUR', locale)

  return (
    <>
      <Head title={t('payments.detail_title')} />
      <PageHeading
        title={t('payments.detail_title')}
        actions={<Button plain href="/payments">{t('common.back')}</Button>}
      />

      <div className="mt-6 flex flex-col gap-6">
        <GatewayNotice state={gateway} />

        {gateway.configured && (
          <Deferred
            data="payment"
            fallback={
              <>
                <CardSkeleton lines={2} />
                <CardSkeleton lines={6} />
              </>
            }
          >
            {payment?.error ? (
              <GatewayNotice state={{ ...gateway, error: payment.error }} />
            ) : record ? (
              <>
                <Card>
                  <CardTitle>{t('payments.amounts')}</CardTitle>
                  <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <Amount label={t('payments.amount')} value={money(record.amount)} />
                    <Amount label={t('payments.captured')} value={money(record.captured_amount)} />
                    <Amount label={t('payments.refunded')} value={money(record.refunded_amount)} />
                    <Amount label={t('payments.reversed')} value={money(record.reversed_amount)} />
                  </dl>
                </Card>

                <Card>
                  <CardTitle>{t('payments.details')}</CardTitle>
                  <DescriptionList className="mt-2">
                    <DescriptionTerm>{t('payments.columns.status')}</DescriptionTerm>
                    <DescriptionDetails>
                      <TransactionStatusBadge status={record.status} family={record.status_family} />
                    </DescriptionDetails>

                    <DescriptionTerm>{t('payments.columns.transaction')}</DescriptionTerm>
                    <DescriptionDetails className="font-mono text-sm">{record.transaction_id}</DescriptionDetails>

                    <DescriptionTerm>{t('payments.columns.method')}</DescriptionTerm>
                    <DescriptionDetails>{record.payment_method_name}</DescriptionDetails>

                    <DescriptionTerm>{t('payments.columns.date')}</DescriptionTerm>
                    <DescriptionDetails>{formatDateTime(record.created_at, locale)}</DescriptionDetails>

                    <DescriptionTerm>{t('payments.order_id')}</DescriptionTerm>
                    <DescriptionDetails>{record.order_id || '—'}</DescriptionDetails>

                    <DescriptionTerm>{t('payments.reference')}</DescriptionTerm>
                    <DescriptionDetails>{record.merchant_reference || '—'}</DescriptionDetails>

                    {record.card_last_four && (
                      <>
                        <DescriptionTerm>{t('payments.card')}</DescriptionTerm>
                        <DescriptionDetails>
                          {record.card_brand ? `${record.card_brand} ` : ''}•••• {record.card_last_four}
                        </DescriptionDetails>
                      </>
                    )}
                  </DescriptionList>
                </Card>

                {canWrite && <PaymentActions payment={record} />}
              </>
            ) : null}
          </Deferred>
        )}

        <TransactionActivity items={activity} />
      </div>
    </>
  )
}

function Amount({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-text/60">{label}</dt>
      <dd className="mt-1 font-heading text-h3 font-bold text-text-strong">{value}</dd>
    </div>
  )
}

PaymentShow.layout = withAppLayout

export default PaymentShow
